// FILE: apps/admin/src/components/AttachmentGallery.tsx
// VERSION: 1.0.0
// START_MODULE_CONTRACT
//   PURPOSE: Reusable Admin Panel photo attachment gallery, inline count badge, retention formatter, and portaled lightbox modal for 14-day MinIO attachments.
//   SCOPE: MediaAttachmentItem, describeAttachmentRetention, AttachmentCountBadge, AttachmentGallery.
//   DEPENDS: none
//   LINKS: M-ADMIN
//   ROLE: RUNTIME
//   MAP_MODE: EXPORTS
// END_MODULE_CONTRACT
//
// START_MODULE_MAP
//   MediaAttachmentItem - Structural shape of a 14-day MinIO photo attachment DTO
//   describeAttachmentRetention - Formats remaining retention days or expired tombstone label
//   AttachmentCountBadge - Compact inline SVG photo badge for Admin queue tables
//   AttachmentGallery - Thumbnail grid with expired tombstones and portaled lightbox preview
// END_MODULE_MAP

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

export interface MediaAttachmentItem {
  id: string;
  fileName: string | null;
  mimeType: string;
  sizeBytes: number;
  url: string | null;
  expiresAt: string;
  expired: boolean;
  createdAt: string;
}

const DAY_MS = 86_400_000;

const PhotoSvg = ({ size = 12 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    style={{ flexShrink: 0 }}
  >
    <rect x="3" y="3" width="18" height="18" rx="3" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <path d="m21 15-5-5L5 21" />
  </svg>
);

const ClockExpiredSvg = ({ size = 18 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    style={{ flexShrink: 0, opacity: 0.7 }}
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </svg>
);

const ExternalSvg = ({ size = 13 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    style={{ flexShrink: 0 }}
  >
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
);

// START_CONTRACT: describeAttachmentRetention
//   PURPOSE: Format a human-readable Russian retention status label for an active or 14-day-expired MinIO attachment.
//   INPUTS: att: MediaAttachmentItem, nowMs?: number
//   OUTPUTS: string
//   SIDE_EFFECTS: none
//   LINKS: M-ADMIN
// END_CONTRACT: describeAttachmentRetention
export function describeAttachmentRetention(
  att: MediaAttachmentItem,
  nowMs: number = Date.now(),
): string {
  // START_BLOCK_DESCRIBE_ATTACHMENT_RETENTION
  if (att.expired || !att.url) {
    return 'Удалено (14 дн. истекло)';
  }
  const expiresMs = new Date(att.expiresAt).getTime();
  if (!Number.isFinite(expiresMs) || expiresMs <= nowMs) {
    return 'Удалено (14 дн. истекло)';
  }
  const daysLeft = Math.max(1, Math.ceil((expiresMs - nowMs) / DAY_MS));
  return `Хранится ещё ${daysLeft} дн.`;
  // END_BLOCK_DESCRIBE_ATTACHMENT_RETENTION
}

// START_CONTRACT: AttachmentCountBadge
//   PURPOSE: Render a compact inline SVG badge showing how many photo attachments belong to a row in Admin queues.
//   INPUTS: props: { count?: number; attachments?: MediaAttachmentItem[] }
//   OUTPUTS: React element | null
//   SIDE_EFFECTS: none
//   LINKS: M-ADMIN
// END_CONTRACT: AttachmentCountBadge
export function AttachmentCountBadge({
  count,
  attachments,
}: {
  count?: number;
  attachments?: MediaAttachmentItem[];
}) {
  // START_BLOCK_ATTACHMENT_COUNT_BADGE
  const total = typeof count === 'number' ? count : (attachments?.length ?? 0);
  if (total <= 0) {
    return null;
  }
  const activeCount = attachments
    ? attachments.filter((a) => !a.expired && Boolean(a.url)).length
    : total;
  return (
    <span
      className="chip info"
      title={
        attachments && activeCount < total
          ? `Фото-вложений: ${total} (активных: ${activeCount})`
          : `Фото-вложений: ${total}`
      }
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        marginTop: 4,
        marginLeft: 4,
      }}
    >
      <PhotoSvg size={11} />
      <span>{total} фото</span>
    </span>
  );
  // END_BLOCK_ATTACHMENT_COUNT_BADGE
}

// START_CONTRACT: AttachmentGallery
//   PURPOSE: Render thumbnail cards for 14-day MinIO photo attachments with expired tombstones and portaled lightbox modal.
//   INPUTS: props: { attachments?: MediaAttachmentItem[]; title?: string }
//   OUTPUTS: React element | null
//   SIDE_EFFECTS: Binds Escape key listener while lightbox modal is open
//   LINKS: M-ADMIN
// END_CONTRACT: AttachmentGallery
export function AttachmentGallery({
  attachments,
  title,
}: {
  attachments?: MediaAttachmentItem[];
  title?: string;
}) {
  // START_BLOCK_ATTACHMENT_GALLERY
  const [preview, setPreview] = useState<MediaAttachmentItem | null>(null);

  useEffect(() => {
    if (!preview) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setPreview(null);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [preview]);

  if (!attachments || attachments.length === 0) {
    return null;
  }

  return (
    <div
      style={{
        padding: 12,
        borderRadius: 10,
        background: 'var(--bg-2)',
        border: '1px solid var(--hair)',
        marginBottom: 14,
      }}
    >
      <div
        className="row"
        style={{
          justifyContent: 'space-between',
          marginBottom: 10,
          fontSize: 12,
        }}
      >
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontWeight: 600,
            color: 'var(--ink-soft)',
          }}
        >
          <PhotoSvg size={13} />
          <span>{title ?? `Фото-вложения (${attachments.length})`}</span>
        </span>
        <span className="muted" style={{ fontSize: 11 }}>
          Автоудаление из MinIO через 14 дн.
        </span>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(112px, 1fr))',
          gap: 10,
        }}
      >
        {attachments.map((att) => {
          const retentionLabel = describeAttachmentRetention(att);
          const isActive = !att.expired && Boolean(att.url);
          if (!isActive) {
            return (
              <div
                key={att.id}
                style={{
                  borderRadius: 9,
                  border: '1px dashed var(--hair-2)',
                  background: 'rgba(255, 255, 255, 0.02)',
                  padding: '12px 8px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  minHeight: 112,
                  gap: 6,
                }}
              >
                <ClockExpiredSvg size={18} />
                <div className="muted" style={{ fontSize: 10.5, lineHeight: 1.3 }}>
                  {retentionLabel}
                </div>
              </div>
            );
          }

          const displayName = att.fileName || 'Вложение';
          return (
            <button
              key={att.id}
              type="button"
              onClick={() => setPreview(att)}
              style={{
                padding: 0,
                border: '1px solid var(--hair-2)',
                borderRadius: 9,
                overflow: 'hidden',
                background: 'var(--bg-1)',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                textAlign: 'left',
                color: 'var(--ink)',
              }}
              title={`${displayName} · ${retentionLabel}`}
            >
              <div
                style={{
                  width: '100%',
                  height: 84,
                  background: '#0f1520',
                  overflow: 'hidden',
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                <img
                  src={att.url ?? undefined}
                  alt={displayName}
                  loading="lazy"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                  }}
                />
              </div>
              <div
                style={{
                  padding: '5px 7px',
                  fontSize: 10.5,
                  color: 'var(--ink-soft)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  width: '100%',
                }}
              >
                {retentionLabel}
              </div>
            </button>
          );
        })}
      </div>

      {preview &&
        preview.url &&
        typeof document !== 'undefined' &&
        createPortal(
          <div className="modal-backdrop" onClick={() => setPreview(null)}>
            <div
              className="modal-card"
              style={{ maxWidth: 760, width: '92vw' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className="row"
                style={{
                  justifyContent: 'space-between',
                  marginBottom: 12,
                  gap: 12,
                  flexWrap: 'wrap',
                }}
              >
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>
                    {preview.fileName || 'Вложение'}
                  </div>
                  <div className="muted" style={{ fontSize: 11.5, marginTop: 2 }}>
                    {Math.max(1, Math.round(preview.sizeBytes / 1024))} КБ ·{' '}
                    {describeAttachmentRetention(preview)}
                  </div>
                </div>
                <div className="row" style={{ gap: 8 }}>
                  <a
                    href={preview.url}
                    target="_blank"
                    rel="noreferrer"
                    className="btn sm"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                  >
                    <ExternalSvg size={13} />
                    <span>Открыть оригинал</span>
                  </a>
                  <button type="button" className="btn sm ghost" onClick={() => setPreview(null)}>
                    Закрыть
                  </button>
                </div>
              </div>

              <div
                style={{
                  borderRadius: 10,
                  overflow: 'hidden',
                  background: '#0d131d',
                  border: '1px solid var(--hair)',
                  display: 'grid',
                  placeItems: 'center',
                  maxHeight: '72vh',
                }}
              >
                <img
                  src={preview.url}
                  alt={preview.fileName || 'Вложение'}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '72vh',
                    objectFit: 'contain',
                    display: 'block',
                  }}
                />
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
  // END_BLOCK_ATTACHMENT_GALLERY
}
